import { useContext, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trophy, LogOut, QrCode } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from '../../../axios-config';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import AnimatedBackground from '../Kiosk/AnimatedBackground';
import HouseParticles from '../Kiosk/HouseParticles';
import ScanSuccess from '../Kiosk/ScanSuccess';
import QRScanner from '../../../components/Acorn/QRScanner';
import { houseKioskRoutes } from '../../routes';
import { UserContext } from '../../../contexts/UserContext';
import { getHouseIdFromProfile } from '../../utils';

interface MascotViewProps {
  houseId?: string;
}

type ScanSuccessResponse = {
  acornsRedeemed: number;
  points: number;
  pointsAdded: number;
  badges: Array<{
    BadgeName: string;
    BadgeDescription: string;
    BadgeId: string;
    CurrentXp: number;
    LevelNumber: number | null;
    LevelName: string | null;
    BadgeImageUrl: string;
    LevelImageUrl: string | null;
  }>;
};

export default function MascotView({ houseId: houseIdOverride }: MascotViewProps) {
  const { houseId } = useParams<{ houseId?: string }>();
  const { logout } = useAuth();
  const { userProfile } = useContext(UserContext);
  const { getHouse, activeHouses } = useApp();
  const navigate = useNavigate();

  const [showSuccess, setShowSuccess] = useState(false);
  const [scanResponse, setScanResponse] = useState<ScanSuccessResponse | undefined>(undefined);
  const [showQRScanner, setShowQRScanner] = useState(false);

  const handleSuccessComplete = useCallback(() => {
    setShowSuccess(false);
    setScanResponse(undefined);
  }, []);

  const resolvedHouseId =
    houseIdOverride || houseId || getHouseIdFromProfile(userProfile) || activeHouses[0]?.id || '';
  const house = getHouse(resolvedHouseId);

  if (!house) {
    return null; // navigate(houseKioskRoutes.leaderboard);
  }

  const handleLogout = () => {
    logout();
    navigate(houseKioskRoutes.login);
  };

  const handleQRScanCode = async (code: string): Promise<ScanSuccessResponse | null> => {
    try {
      // First, redeem the coupon through the API
      const response = await axios.post(`/api/Acorns/Redeem?acornCode=${code}`);

      // Do NOT mutate local points here.
      // The notification hub emits HousePointsUpdated/MascotRedeemAcorn; applying a local delta
      // causes double-counting when the hub event arrives.
      return response.data as ScanSuccessResponse;
    } catch (error) {
      console.error('Error processing coupon:', error);
      return null;
    }
  };

  const handleScannerSuccess = (response?: ScanSuccessResponse) => {
    setScanResponse(response);
    setShowQRScanner(false);
    setShowSuccess(true);
  };

  return (
    <div className="relative min-h-screen overflow-hidden font-sans">
      <AnimatedBackground houseId={house.id} />
      <HouseParticles houseId={house.id} />

      <div className="relative z-10 min-h-screen flex flex-col p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleLogout}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20"
          >
            <LogOut size={24} />
          </button>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <span className="text-6xl md:text-8xl">{house.icon}</span>
          </motion.div>

          <button
            onClick={() => navigate(houseKioskRoutes.leaderboard)}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20"
          >
            <Trophy size={24} />
          </button>
        </div>

        {/* House Name */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-4xl md:text-6xl font-bold text-center text-white tracking-wider mb-4"
          style={{ textShadow: `0 0 40px ${house.color}` }}
        >
          {house.name.toUpperCase()}
        </motion.h1>

        {/* Points Display */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex-1 flex flex-col items-center justify-center"
        >
          <div className="backdrop-blur-lg bg-white/10 rounded-3xl p-8 md:p-12 border-2 border-white/20">
            <p className="text-white/60 text-lg text-center mb-2">TOTAL POINTS</p>
            <p
              className="text-6xl md:text-8xl font-bold text-center"
              style={{
                color: house.accent,
                textShadow: `0 0 30px ${house.color}`,
              }}
            >
              {house.points.toLocaleString()}
            </p>
          </div>
        </motion.div>

        {/* Scan Button */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col items-center space-y-6"
        >
          <button
            onClick={() => setShowQRScanner(true)}
            className="relative group"
          >
            <div
              className="absolute inset-0 rounded-full blur-xl opacity-60 group-hover:opacity-80 transition-opacity"
              style={{ backgroundColor: house.color }}
            />
            <div
              className="relative backdrop-blur-lg bg-white/20 hover:bg-white/30 rounded-full p-8 border-4 transition-all duration-300 hover:scale-110"
              style={{ borderColor: house.color }}
            >
              <QrCode size={64} className="text-white" />
            </div>
          </button>
          <p className="text-white/60 text-lg">Tap to Scan Coupon</p>
        </motion.div>

        {/* View Standings Button */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex justify-center"
        >
          <button
            onClick={() => navigate(houseKioskRoutes.leaderboard)}
            className="backdrop-blur-lg bg-white/20 hover:bg-white/30 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 border-2 border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center space-x-3"
          >
            <Trophy size={24} />
            <span className="text-lg">VIEW ALL STANDINGS</span>
          </button>
        </motion.div>
      </div>

      {showQRScanner && (
        <QRScanner
          onClose={() => setShowQRScanner(false)}
          onRedeemSuccess={handleScannerSuccess}
          mode="redeem"
          onCode={handleQRScanCode}
          title="Scan Coupon"
          submitLabel="Redeem"
        />
      )}

      {showSuccess && (
        <ScanSuccess
          house={house}
          response={scanResponse}
          onComplete={handleSuccessComplete}
        />
      )}
    </div>
  );
}
